import { CheckCircle2Icon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useLocationWatchmanForm } from './useLocationWatchmanForm'

export const LOCATION_WATCHMAN_TITLE = 'Hlídat volné termíny'

interface LocationWatchmanDialogProps {
  slug: string
  unitId: number
  /** Where the dialog was opened from, tracked with the subscription. */
  source: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** The e-mail form for watching a unit's free dates — a drawer on mobile, a sheet on desktop. */
export function LocationWatchmanDialog({ slug, unitId, source, open, onOpenChange }: LocationWatchmanDialogProps) {
  const isMobile = useIsMobile()
  const { email, setEmail, status, submit } = useLocationWatchmanForm(slug, unitId, source)

  const description = (
    <>
      <span className="block">Zaujalo vás tohle místo, ale aktuálně se vám žádný termín nehodí?</span>
      <span className="mt-2 block">
        Zadejte svůj e-mail a dáme vám vědět hned, jakmile se objeví nové volno. Budete tak mezi prvními, kdo se o
        uvolněném termínu dozví.
      </span>
    </>
  )

  const formContent =
    status === 'success' ? (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex justify-center rounded-full bg-green-50 p-2 text-green-600">
          <CheckCircle2Icon className="size-8" />
        </div>
        <p className="text-sm text-slate-600">
          Hotovo! Ozveme se e-mailem, jakmile se u tohoto místa uvolní nový termín.
        </p>
      </div>
    ) : (
      <form onSubmit={submit} className="space-y-3">
        <Input
          type="email"
          name="email"
          required
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the only field of a sheet/drawer the user just opened to type an e-mail
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Zadejte Váš e-mail"
        />
        <Button type="submit" variant="red" className="w-full" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Odesílám…' : 'Chci hlídat termíny'}
        </Button>
        {status === 'error' && <p className="text-red text-sm">Něco se nepovedlo, zkuste to prosím znovu.</p>}
      </form>
    )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
        <DrawerContent className="max-h-[90dvh]">
          <DrawerHeader>
            <DrawerTitle>{LOCATION_WATCHMAN_TITLE}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">{formContent}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader className="border-b border-slate-100 p-6">
          <SheetTitle className="text-xl">{LOCATION_WATCHMAN_TITLE}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-6">{formContent}</div>
      </SheetContent>
    </Sheet>
  )
}
